import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Users, LayoutGrid, Search, FolderHeart, MessageSquare, 
  Package, Bell, TrendingUp, Eye, Calendar, ArrowUpRight,
  Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'stone' }) => (
  <div className="bg-white border border-stone-200 p-6">
    <div className="flex items-start justify-between mb-4">
      <div className={`p-3 bg-${color}-100 rounded-lg`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      {trend && (
        <span className={`text-sm flex items-center gap-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          <ArrowUpRight className="w-4 h-4" />
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <h3 className="text-3xl font-semibold text-stone-900 mb-1">{value?.toLocaleString() || 0}</h3>
    <p className="text-sm text-stone-500">{title}</p>
    {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
  </div>
);

const AdminStatistics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await axios.get(`${API}/admin/statistics/overview`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Breadcrumb 
            items={[{ label: 'Admin', href: '/admin' }]} 
            currentPage="Statistics" 
          />
          
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-light text-stone-900">Platform Statistics</h1>
              <p className="text-stone-500 mt-1">
                Last updated: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => fetchStats(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* User Statistics */}
          <section className="mb-12">
            <h2 className="text-xl font-medium text-stone-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" /> Users
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total Users" 
                value={stats?.users?.total} 
                icon={Users}
              />
              <StatCard 
                title="New This Week" 
                value={stats?.users?.new_this_week}
                subtitle="Last 7 days"
                icon={TrendingUp}
                color="green"
              />
              <StatCard 
                title="New This Month" 
                value={stats?.users?.new_this_month}
                subtitle="Last 30 days"
                icon={Calendar}
              />
              <StatCard 
                title="Pending Approval" 
                value={stats?.users?.pending_approval}
                icon={Users}
                color="amber"
              />
            </div>
            
            {/* Users by Role */}
            <div className="mt-4 bg-white border border-stone-200 p-6">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Users by Role</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {stats?.users?.by_role && Object.entries(stats.users.by_role).map(([role, count]) => (
                  <div key={role} className="text-center p-3 bg-stone-50">
                    <p className="text-2xl font-semibold text-stone-900">{count}</p>
                    <p className="text-xs text-stone-500 capitalize">{role.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* Content Statistics */}
          <section className="mb-12">
            <h2 className="text-xl font-medium text-stone-900 mb-4 flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" /> Content
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total Materials" 
                value={stats?.materials?.total}
                icon={LayoutGrid}
              />
              <StatCard 
                title="Total Projects" 
                value={stats?.projects?.total}
                subtitle={`${stats?.projects?.this_week || 0} this week`}
                icon={FolderHeart}
              />
              <StatCard 
                title="Collections" 
                value={stats?.collections?.total}
                subtitle={`${stats?.collections?.public || 0} public`}
                icon={FolderHeart}
              />
              <StatCard 
                title="Inspiration Projects" 
                value={stats?.inspiration?.total_projects}
                subtitle={`${stats?.inspiration?.total_views || 0} total views`}
                icon={Eye}
              />
            </div>
          </section>
          
          {/* Engagement Statistics */}
          <section className="mb-12">
            <h2 className="text-xl font-medium text-stone-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Engagement
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Visual Searches" 
                value={stats?.visual_search?.total}
                subtitle={`${stats?.visual_search?.this_week || 0} this week`}
                icon={Search}
                color="blue"
              />
              <StatCard 
                title="Saved Materials" 
                value={stats?.engagement?.total_saved_materials}
                icon={FolderHeart}
                color="red"
              />
              <StatCard 
                title="Avg Items/Project" 
                value={stats?.projects?.avg_items_per_project}
                icon={LayoutGrid}
              />
              <StatCard 
                title="Projects with Items" 
                value={stats?.projects?.with_items}
                icon={LayoutGrid}
              />
            </div>
          </section>
          
          {/* Requests Statistics */}
          <section className="mb-12">
            <h2 className="text-xl font-medium text-stone-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Requests & Support
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Advisor Requests" 
                value={stats?.advisor?.total_requests}
                subtitle={`${stats?.advisor?.pending || 0} pending`}
                icon={MessageSquare}
              />
              <StatCard 
                title="Sample Requests" 
                value={stats?.samples?.total_requests}
                subtitle={`${stats?.samples?.pending || 0} pending`}
                icon={Package}
              />
              <StatCard 
                title="Client Invitations" 
                value={stats?.client_invitations?.total}
                subtitle={`${stats?.client_invitations?.accepted || 0} accepted`}
                icon={Users}
                color="green"
              />
              <StatCard 
                title="Unread Notifications" 
                value={stats?.notifications?.unread}
                subtitle={`${stats?.notifications?.total || 0} total`}
                icon={Bell}
                color="amber"
              />
            </div>
          </section>
          
          {/* Materials by Type */}
          {stats?.materials?.by_type && (
            <section className="mb-12">
              <h2 className="text-xl font-medium text-stone-900 mb-4">Materials by Type</h2>
              <div className="bg-white border border-stone-200 p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {Object.entries(stats.materials.by_type)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => (
                      <div key={type} className="text-center p-4 bg-stone-50">
                        <p className="text-2xl font-semibold text-stone-900">{count}</p>
                        <p className="text-xs text-stone-500 capitalize">{type}</p>
                      </div>
                    ))
                  }
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminStatistics;
